/**
 * Boutiquen locator — fetches boutiques.json and renders Leaflet map + grouped list.
 */
(function () {
  'use strict';

  async function init() {
    const section = document.querySelector('.bl');
    if (!section) return;
    if (typeof L === 'undefined') {
      setTimeout(init, 50);
      return;
    }

    const dataUrl = section.getAttribute('data-bl-data-url');
    const inlineEl = section.querySelector('[data-bl-inline-data]');
    const labelsEl = section.querySelector('[data-bl-labels]');

    const mapEl = section.querySelector('[data-bl-map]');
    const searchInput = section.querySelector('[data-bl-search]');
    const searchBtn = section.querySelector('[data-bl-search-btn]');
    const geoBtn = section.querySelector('[data-bl-geo]');
    const listEl = section.querySelector('[data-bl-list]');

    // Editable labels (fall back to German defaults)
    const LABELS_DEFAULTS = {
      phonePrefix: 'Tel:',
      hoursHeading: 'Öffnungszeiten',
      hoursSeparator: '-',
      closed: 'Geschlossen',
      details: 'Details ansehen',
      directions: 'Wegbeschreibung',
      loading: 'Boutiquen werden geladen…',
      loadError: 'Boutiquen konnten nicht geladen werden.',
      noData: 'Keine Boutiquen konfiguriert.',
      geoError: 'Standort konnte nicht ermittelt werden:',
      geoUnsupported: 'Geolocation wird von diesem Browser nicht unterstützt.',
      geoSearching: 'Suche…',
      geoYouAreHere: 'Sie sind hier',
      days: {
        Monday: 'Montag', Tuesday: 'Dienstag', Wednesday: 'Mittwoch',
        Thursday: 'Donnerstag', Friday: 'Freitag', Saturday: 'Samstag', Sunday: 'Sonntag',
      },
    };
    let LABELS = LABELS_DEFAULTS;
    if (labelsEl) {
      try {
        const parsed = JSON.parse(labelsEl.textContent || '{}');
        LABELS = Object.assign({}, LABELS_DEFAULTS, parsed);
        LABELS.days = Object.assign({}, LABELS_DEFAULTS.days, parsed.days || {});
      } catch (e) { /* keep defaults */ }
    }

    let boutiques = [];

    // Prefer inline data emitted by the section (editable via Theme Editor).
    // Fall back to fetching the legacy boutiques.json asset.
    if (inlineEl) {
      const text = (inlineEl.textContent || '').trim();
      if (text && text !== '[]') {
        try {
          boutiques = JSON.parse(text);
        } catch (e) {
          console.error('[bl] failed to parse inline boutique data', e);
        }
      }
    }

    if (boutiques.length === 0 && dataUrl) {
      try {
        const res = await fetch(dataUrl);
        boutiques = await res.json();
      } catch (e) {
        console.error('[bl] failed to load boutiques.json', e);
        listEl.innerHTML = `<div class="bl__loading">${escapeHtml(LABELS.loadError)}</div>`;
        return;
      }
    }

    if (boutiques.length === 0) {
      listEl.innerHTML = `<div class="bl__loading">${escapeHtml(LABELS.noData)}</div>`;
      return;
    }

    // ===== Build list =====
    const byCity = new Map();
    boutiques.forEach((b) => {
      const city = (b.city || 'Andere').trim();
      if (!byCity.has(city)) byCity.set(city, []);
      byCity.get(city).push(b);
    });
    const cities = Array.from(byCity.keys()).sort((a, b) => a.localeCompare(b));

    listEl.innerHTML = '';
    cities.forEach((city) => {
      const group = document.createElement('details');
      group.className = 'bl__city-group';
      group.open = true;
      group.setAttribute('data-bl-city', city.toLowerCase());

      const bs = byCity.get(city);
      const summary = document.createElement('summary');
      summary.className = 'bl__city-summary';
      summary.innerHTML = `<span>${escapeHtml(city)}</span><span class="bl__city-count">${bs.length}</span>`;
      group.appendChild(summary);

      const ul = document.createElement('ul');
      ul.className = 'bl__boutiques';
      bs.forEach((b) => {
        const li = document.createElement('li');
        li.className = 'bl__boutique';
        li.setAttribute('data-bl-item', '');
        li.dataset.name = (b.name || '').toLowerCase();
        li.dataset.city = (b.city || '').toLowerCase();
        li.dataset.postal = b.postal || '';
        if (b.lat != null) li.dataset.lat = b.lat;
        if (b.lng != null) li.dataset.lng = b.lng;

        const addrLine = [b.street, b.postal, b.city].filter(Boolean).join(', ');
        const phoneLine = b.phone ? `${escapeHtml(LABELS.phonePrefix)} ${escapeHtml(b.phone)}` : '';
        const rawDetailUrl = b.url_source || (b.handle ? `/pages/boutique-${b.handle}` : null);
        // Always carry the storefront's locale prefix so clicking 'View details'
        // on /en/pages/boutiquen lands on /en/pages/boutique-basel — never the
        // default-locale (German) page.
        const detailUrl = rawDetailUrl ? withLocalePrefix(rawDetailUrl) : null;
        const directionsHtml = b.lat != null && b.lng != null
          ? `<a class="bl__b-directions" target="_blank" rel="noopener" href="https://www.google.com/maps/dir/?api=1&destination=${b.lat},${b.lng}">${escapeHtml(LABELS.directions)} →</a>`
          : '';
        const detailsHtml = detailUrl
          ? `<a class="bl__b-details" href="${detailUrl}">${escapeHtml(LABELS.details)} →</a>`
          : '';
        const thumbUrl = b.image
          ? (b.image + (b.image.indexOf('?') >= 0 ? '&width=120' : '?width=120'))
          : null;
        const pinHtml = thumbUrl
          ? `<span class="bl__b-thumb" aria-hidden="true" style="background-image:url('${thumbUrl}')"></span>`
          : `<span class="bl__b-pin" aria-hidden="true">
              <svg viewBox="0 0 48 56">
                <path d="M24 0C10.7 0 0 10.7 0 24c0 17 24 32 24 32s24-15 24-32C48 10.7 37.3 0 24 0z" fill="#556b2a"/>
                <circle cx="24" cy="22" r="10" fill="#ffffff"/>
                <text x="24" y="26" text-anchor="middle" font-family="Georgia, serif" font-size="11" font-weight="700" fill="#556b2a">PT</text>
              </svg>
             </span>`;

        li.innerHTML = `
          <details class="bl__b-card">
            <summary class="bl__b-summary">
              ${pinHtml}
              <span class="bl__b-text">
                <span class="bl__b-name">${escapeHtml(b.name)}</span>
                ${addrLine ? `<span class="bl__b-line">${escapeHtml(addrLine)}</span>` : ''}
                ${phoneLine ? `<span class="bl__b-line bl__b-line--phone">${phoneLine}</span>` : ''}
              </span>
              <span class="bl__b-caret" aria-hidden="true">
                <svg viewBox="0 0 12 8"><path d="M1 2 L6 7 L11 2" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>
              </span>
            </summary>
            <div class="bl__b-body">
              <div class="bl__b-hours-slot" data-bl-hours-slot ${detailUrl ? `data-detail-url="${detailUrl}"` : ''}></div>
              <div class="bl__b-body-actions">
                ${detailsHtml}
                ${directionsHtml}
              </div>
            </div>
          </details>
        `;
        ul.appendChild(li);
      });
      group.appendChild(ul);
      listEl.appendChild(group);
    });

    const items = Array.from(section.querySelectorAll('[data-bl-item]'));
    const cityGroups = Array.from(section.querySelectorAll('[data-bl-city]'));

    // ===== Hours hydration =====
    // Each card's hours block stays empty in the initial render. The locator
    // fetches the matching detail page and reads its <script data-pdt-boutique-hours>
    // JSON island. This keeps the detail page section the single source of truth.
    hydrateHours(section, LABELS);

    // ===== Map =====
    const map = L.map(mapEl, {
      scrollWheelZoom: true,
      worldCopyJump: true,
      attributionControl: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);

    // Custom fullscreen button (native Fullscreen API)
    const FullscreenBtn = L.Control.extend({
      options: { position: 'topright' },
      onAdd: function () {
        const btn = L.DomUtil.create('button', 'bl-fs-btn');
        btn.type = 'button';
        btn.setAttribute('aria-label', 'Vollbild umschalten');
        btn.innerHTML = `
          <svg class="bl-fs-open" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M2 6 V2 H6 M10 2 H14 V6 M14 10 V14 H10 M6 14 H2 V10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg class="bl-fs-close" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M6 2 V6 H2 M10 2 V6 H14 M14 10 H10 V14 M2 10 H6 V14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        L.DomEvent.disableClickPropagation(btn);
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const target = mapEl;
          if (!document.fullscreenElement) {
            (target.requestFullscreen || target.webkitRequestFullscreen || target.msRequestFullscreen).call(target);
          } else {
            (document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen).call(document);
          }
        });
        return btn;
      },
    });
    new FullscreenBtn().addTo(map);

    // Resize map when entering/leaving fullscreen so tiles reflow
    function onFsChange() {
      const isFs = document.fullscreenElement === mapEl;
      mapEl.classList.toggle('is-fullscreen', isFs);
      setTimeout(() => map.invalidateSize(), 120);
    }
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);

    const olivePin = L.divIcon({
      className: 'bl-pin',
      html: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="28" height="36"><path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill="#556b2a"/><circle cx="12" cy="12" r="4.5" fill="#ffffff"/></svg>',
      iconSize: [28, 36],
      iconAnchor: [14, 36],
      popupAnchor: [0, -32],
    });

    const markers = [];
    const validPoints = [];
    boutiques.forEach((b) => {
      if (b.lat == null || b.lng == null) return;
      const m = L.marker([b.lat, b.lng], { icon: olivePin }).addTo(map);
      const addr = [b.street, b.postal, b.city].filter(Boolean).join(', ');
      const rawDetailUrl = b.url_source || (b.handle ? `/pages/boutique-${b.handle}` : null);
      const detailUrl = rawDetailUrl ? withLocalePrefix(rawDetailUrl) : null;
      const link = detailUrl ? `<a class="bl-popup-link" href="${detailUrl}">${escapeHtml(LABELS.details)} →</a>` : '';
      const thumbUrl = b.image
        ? (b.image + (b.image.indexOf('?') >= 0 ? '&width=260' : '?width=260'))
        : null;
      const img = thumbUrl ? `<div class="bl-popup-img" style="background-image:url('${thumbUrl}')"></div>` : '';
      m.bindPopup(`
        <div class="bl-popup">
          ${img}
          <div class="bl-popup-name">${escapeHtml(b.name)}</div>
          <div class="bl-popup-addr">${escapeHtml(addr)}</div>
          ${link}
        </div>
      `);
      markers.push({ marker: m, lat: b.lat, lng: b.lng });
      validPoints.push([b.lat, b.lng]);
    });

    if (validPoints.length) {
      map.fitBounds(L.latLngBounds(validPoints), { padding: [40, 40] });
    } else {
      map.setView([46.8, 8.2], 7);
    }

    // ===== Search =====
    function applyFilter(query) {
      const q = (query || '').trim().toLowerCase();
      items.forEach((it) => {
        const haystack = [it.dataset.name, it.dataset.city, it.dataset.postal].join(' ').toLowerCase();
        const match = !q || haystack.indexOf(q) !== -1;
        it.hidden = !match;
      });
      cityGroups.forEach((g) => {
        const hasVisible = g.querySelectorAll('[data-bl-item]:not([hidden])').length > 0;
        g.hidden = !hasVisible;
        if (q) g.open = hasVisible;
      });
    }

    function runSearchAndPan() {
      const q = searchInput.value;
      applyFilter(q);
      if (!q) return;
      const firstVisible = items.find((it) => !it.hidden && it.dataset.lat && it.dataset.lng);
      if (firstVisible) {
        const lat = parseFloat(firstVisible.dataset.lat);
        const lng = parseFloat(firstVisible.dataset.lng);
        if (!isNaN(lat) && !isNaN(lng)) map.setView([lat, lng], 13, { animate: true });
      }
    }

    searchInput.addEventListener('input', () => applyFilter(searchInput.value));
    searchInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') runSearchAndPan(); });
    searchBtn.addEventListener('click', runSearchAndPan);

    applyFilter('');

    // Pre-fill search from ?q= URL param (used by the directory links below the map)
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('q');
      if (q) {
        searchInput.value = q;
        runSearchAndPan();
      }
    } catch (e) { /* noop */ }

    // ===== Geolocation =====
    geoBtn.addEventListener('click', () => {
      if (!navigator.geolocation) {
        alert(LABELS.geoUnsupported);
        return;
      }
      geoBtn.disabled = true;
      const original = geoBtn.innerHTML;
      geoBtn.textContent = LABELS.geoSearching;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          map.setView([latitude, longitude], 11, { animate: true });
          L.circleMarker([latitude, longitude], {
            radius: 8,
            color: '#a01e3a',
            weight: 3,
            fillColor: '#ffffff',
            fillOpacity: 1,
          }).addTo(map).bindPopup(LABELS.geoYouAreHere).openPopup();
          geoBtn.disabled = false;
          geoBtn.innerHTML = original;
        },
        (err) => {
          alert(`${LABELS.geoError} ${err.message}`);
          geoBtn.disabled = false;
          geoBtn.innerHTML = original;
        }
      );
    });

    // ===== Click a boutique card -> zoom map to its pin =====
    // Use the summary click; don't interfere with native <details> toggle.
    listEl.addEventListener('click', (e) => {
      const summary = e.target.closest('.bl__b-summary');
      if (!summary) return;
      // Don't hijack clicks on interactive children inside the body
      if (e.target.closest('a, button:not(summary)')) return;

      const item = summary.closest('[data-bl-item]');
      if (!item) return;
      const lat = parseFloat(item.dataset.lat);
      const lng = parseFloat(item.dataset.lng);
      if (isNaN(lat) || isNaN(lng)) return;
      map.setView([lat, lng], 15, { animate: true });
      markers.forEach(({ marker, lat: mlat, lng: mlng }) => {
        if (Math.abs(mlat - lat) < 0.0001 && Math.abs(mlng - lng) < 0.0001) {
          marker.openPopup();
        }
      });
      if (window.innerWidth < 900) {
        mapEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  function decodeEntities(s) {
    return String(s || '')
      .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCodePoint(parseInt(h, 16)))
      .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(parseInt(n, 10)))
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  function escapeHtml(s) {
    return decodeEntities(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  }

  const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  function formatHourSlots(slots, labels) {
    if (!Array.isArray(slots) || slots.length === 0) return labels.closed;
    return slots
      .map((s) => `${s.open_hour} ${labels.hoursSeparator} ${s.close_hour}`)
      .join(', ');
  }

  function renderHoursBlock(hours, labels) {
    if (!hours || typeof hours !== 'object') return '';
    const rows = DAY_ORDER
      .filter((d) => hours[d])
      .map((d) => {
        const label = (labels.days && labels.days[d]) || d;
        const slots = hours[d];
        const open = Array.isArray(slots) && slots.length > 0;
        return `<div class="bl__b-day">
          <span class="bl__b-day-name">${escapeHtml(label)}</span>
          <span class="bl__b-day-hours${open ? '' : ' is-closed'}">${escapeHtml(formatHourSlots(slots, labels))}</span>
        </div>`;
      });
    if (rows.length === 0) return '';
    return `<div class="bl__b-hours-block">
      <div class="bl__b-hours-title">${escapeHtml(labels.hoursHeading)}</div>
      <div class="bl__b-hours-list">${rows.join('')}</div>
    </div>`;
  }

  // Renders hours rows using the payload the detail page emits.
  // Payload shape: { days: [{day, is_closed, times, closed_label}, ...], hours_label }
  function renderHoursFromDetail(payload, labels) {
    if (!payload || !Array.isArray(payload.days) || payload.days.length === 0) return '';
    const rows = payload.days.map((d) => {
      const dayLabel = d.day || '';
      const isClosed = !!d.is_closed;
      // Normalise the en-dash from the detail page to a plain hyphen with spaces
      const timesText = isClosed
        ? (d.closed_label || labels.closed)
        : String(d.times || '').replace(/\s*[–-]\s*/, ' - ');
      return `<div class="bl__b-day">
        <span class="bl__b-day-name">${escapeHtml(dayLabel)}</span>
        <span class="bl__b-day-hours${isClosed ? ' is-closed' : ''}">${escapeHtml(timesText)}</span>
      </div>`;
    });
    const heading = payload.hours_label || labels.hoursHeading;
    return `<div class="bl__b-hours-block">
      <div class="bl__b-hours-title">${escapeHtml(heading)}</div>
      <div class="bl__b-hours-list">${rows.join('')}</div>
    </div>`;
  }

  // Fetches each card's detail page and copies its hours data into the card.
  // Uses sessionStorage as a per-session cache so revisits and re-renders
  // don't refetch.
  async function hydrateHours(section, labels) {
    const slots = section.querySelectorAll('[data-bl-hours-slot][data-detail-url]');
    const tasks = Array.from(slots).map(async (slot) => {
      const url = slot.getAttribute('data-detail-url');
      if (!url) return;
      try {
        const payload = await fetchDetailHours(url);
        const html = renderHoursFromDetail(payload, labels);
        if (html) slot.innerHTML = html;
      } catch (e) {
        // Silently ignore — card just shows no hours.
        // console.debug('[bl] could not load hours for', url, e);
      }
    });
    await Promise.all(tasks);
  }

  // Returns the locale prefix for the CURRENT storefront page, e.g.
  //   /en  →  on the English storefront (/en/pages/boutiquen)
  //   /fr  →  on the French storefront
  //   ''   →  on the default-locale storefront (/pages/boutiquen)
  // Used to make sure we fetch the locale-matching detail page so the
  // {{ ... | t }} translations in the JSON island come back in the right
  // language.
  function getStorefrontLocalePrefix() {
    // Shopify.locale is set on the storefront JS globals (e.g. 'de', 'en', 'fr-ca').
    // If the current pathname starts with /<that-locale>, that's our prefix.
    const locale = (window.Shopify && window.Shopify.locale)
      ? String(window.Shopify.locale).toLowerCase()
      : '';
    const segs = window.location.pathname.split('/');
    if (segs.length >= 2 && segs[1] && locale && segs[1].toLowerCase() === locale) {
      return '/' + segs[1];
    }
    // Fall back: any leading /xx or /xx-yy segment looks like a locale.
    if (segs.length >= 2 && /^[a-z]{2}(-[a-z]{2})?$/i.test(segs[1] || '')) {
      return '/' + segs[1];
    }
    return '';
  }

  function withLocalePrefix(url) {
    if (!url || !url.startsWith('/')) return url;
    const prefix = getStorefrontLocalePrefix();
    if (!prefix) return url;
    if (url === prefix || url.startsWith(prefix + '/')) return url;
    return prefix + url;
  }

  async function fetchDetailHours(rawUrl) {
    const url = withLocalePrefix(rawUrl);
    // No client cache: a previous version cached an AM/PM time payload
    // that lingered after the source data was corrected. With only a
    // couple of boutiques per page, an extra ~50 KB fetch per card on
    // first load is fine.
    const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const island = doc.querySelector('script[type="application/json"][data-pdt-boutique-hours]');
    if (!island) throw new Error('No hours payload on ' + url);
    return JSON.parse(island.textContent || '{}');
  }

  // Best-effort cleanup of stale cache entries left by older versions
  // of this script — runs once per page load, harmless if nothing matches.
  try {
    if (window.sessionStorage) {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && (k.indexOf('bl-hours:') === 0 || k.indexOf('bl-hours-v2:') === 0)) {
          sessionStorage.removeItem(k);
        }
      }
    }
  } catch (_) { /* ignore */ }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
